import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams, useParams } from 'react-router-dom';
import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { SearchParamKey } from '@app/shared/models/search-param-enum';
import {
  IRRHurdleCalculationsNavigatedSourcePage,
  SponsorIrrAndHurdleTab,
} from '@app/shared/models/contracts/enums/shared-enums';
import { FocusModeLocationState, FocusModeResult, FocusSelections } from './focus-mode-types';
import { buildFocusModeSearchParams } from './buildFocusModeSearchParams';

export const useFocusMode = (): FocusModeResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { projectId } = useParams();
  const { generatePath, navigate } = usePwcNavigate();

  const focusType = searchParams.get(SearchParamKey.FocusType) as SponsorIrrAndHurdleTab | null;
  const isPausedFlag = searchParams.get(SearchParamKey.FocusPaused) === 'true';

  const selections: FocusSelections | undefined = useMemo(() => {
    if (!focusType) {
      return undefined;
    }

    if (focusType === SponsorIrrAndHurdleTab.SponsorIRR) {
      const caseId = searchParams.get(SearchParamKey.FocusCase);
      const year = searchParams.get(SearchParamKey.FocusYear);
      const multiple = searchParams.get(SearchParamKey.FocusMultiple);
      if (!caseId || !year || !multiple) {
        return undefined;
      }
      return { type: SponsorIrrAndHurdleTab.SponsorIRR, case: caseId, year, multiple };
    }

    if (focusType === SponsorIrrAndHurdleTab.IRRHurdleCalculations) {
      const year = searchParams.get(SearchParamKey.FocusYear);
      const instrument = searchParams.get(SearchParamKey.FocusInstrument);
      const hurdle = searchParams.get(SearchParamKey.FocusHurdle);
      const hurdleTitle = searchParams.get(SearchParamKey.FocusHurdleTitle);
      const source = searchParams.get(
        SearchParamKey.FocusSource
      ) as IRRHurdleCalculationsNavigatedSourcePage | null;
      if (!year || !instrument || !hurdle || !hurdleTitle || !source) {
        return undefined;
      }
      return {
        type: SponsorIrrAndHurdleTab.IRRHurdleCalculations,
        case: searchParams.get(SearchParamKey.FocusCase) ?? undefined,
        year,
        instrument,
        hurdle,
        hurdleTitle,
        source,
      };
    }

    return undefined;
  }, [focusType, searchParams]);

  const isActive = Boolean(focusType) && Boolean(selections) && !isPausedFlag;
  const isPaused = Boolean(focusType) && Boolean(selections) && isPausedFlag;

  const buildFocusRoutePath = useCallback(
    (focusSelections: FocusSelections): string => {
      const basePath = `/${RouteConstants.Projects}/${projectId}/${RouteConstants.SponsorIRRAndHurdle}`;

      if (focusSelections.type === SponsorIrrAndHurdleTab.SponsorIRR) {
        return generatePath(`${basePath}/${RouteConstants.SponsorIRR}/${focusSelections.case}`);
      }

      return focusSelections.case
        ? generatePath(
            `${basePath}/${RouteConstants.IRRHurdleCalculations}/${focusSelections.case}`
          )
        : generatePath(`${basePath}/${RouteConstants.IRRHurdleCalculations}`);
    },
    [generatePath, projectId]
  );

  const enterFocus = useCallback(
    (newSelections: FocusSelections, originPath?: string) => {
      const routePath = buildFocusRoutePath(newSelections);
      const params = buildFocusModeSearchParams(newSelections);
      navigate(`${routePath}?${params}`, {
        state: { returnPath: originPath ?? location.pathname } as FocusModeLocationState,
      });
    },
    [buildFocusRoutePath, location.pathname, navigate]
  );

  const pauseFocus = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(SearchParamKey.FocusPaused, 'true');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const regainFocus = useCallback(() => {
    if (!selections) {
      return;
    }

    const routePath = buildFocusRoutePath(selections);
    const params = buildFocusModeSearchParams(selections);
    navigate(`${routePath}?${params}`);
  }, [selections, buildFocusRoutePath, navigate]);

  const returnToPriorPage = useCallback(() => {
    const state = location.state as FocusModeLocationState | null;
    if (state?.returnPath) {
      navigate(state.returnPath);
      return;
    }

    const basePath = `/${RouteConstants.Projects}/${projectId}`;

    if (selections?.type === SponsorIrrAndHurdleTab.IRRHurdleCalculations && selections.source) {
      const source = selections.source;
      if (source === IRRHurdleCalculationsNavigatedSourcePage.PwermWaterfall) {
        navigate(`${basePath}/${RouteConstants.PwermWaterfall}`);
      } else if (source === IRRHurdleCalculationsNavigatedSourcePage.FirstLastWaterfall) {
        navigate(
          `${basePath}/${RouteConstants.OpmWaterfall}/${RouteConstants.FirstLastSimulations}`
        );
      } else if (source === IRRHurdleCalculationsNavigatedSourcePage.SampleWaterfall) {
        navigate(`${basePath}/${RouteConstants.OpmWaterfall}/${RouteConstants.SampleWaterfall}`);
      } else {
        navigate(`${basePath}/${RouteConstants.PwermWaterfall}`);
      }
    } else {
      navigate(`${basePath}/${RouteConstants.PwermWaterfall}`);
    }
  }, [location.state, navigate, projectId, selections]);

  return {
    isActive,
    isPaused,
    focusType: focusType as SponsorIrrAndHurdleTab | undefined,
    selections,
    enterFocus,
    pauseFocus,
    regainFocus,
    returnToPriorPage,
  };
};
